<template>
  <ig-page-base>
    <div class="box billings__content">
      <div class="product-import-and-export__title-box">
        <h2 class="title has-text-weight-semibold">
          {{ $t('storePages.billings.title') }}
        </h2>
      </div>

      <div class="billings__filters">
        <div class="columns">
          <div class="column is-4">
            <b-datepicker
              v-model="dates"
              :placeholder="$t('storePages.billings.filters.rangeDate')"
              :disabled="loading"
              locale="pt-BR"
              icon="calendar"
              @range-end="changeDates"
              trap-focus
              range>
              <b-button
                label="Limpar"
                type="is-danger"
                icon-left="close"
                outlined
                @click="clearDate" />
            </b-datepicker>
          </div>
          <div class="column is-6">
            <form @submit.prevent="getInvoiceList()">
              <b-field grouped>
                <b-input
                  v-model="filters.query"
                  :placeholder="$t('storePages.billings.filters.query')"
                  expanded />
                <p class="control">
                  <b-button
                    native-type="submit"
                    type="is-primary"
                    :label="$t('storePages.billings.filters.btnSearch')"
                    :loading="loading"
                    :disabled="loading"
                  ></b-button>
                </p>
              </b-field>
            </form>
          </div>
          <div class="column has-text-right">
            <b-button
              type="is-primary"
              icon-left="filter"
              outlined
              :label="$t('storePages.billings.filters.btnFilters')"
              :disabled="loading"
              @click="showFilters = true"
            ></b-button>

            <ig-sidebar-filters
              v-model="showFilters"
              v-slot="{ filters }"
              @submit="applyFilters"
            >
              <b-field
                :label="$t('storePages.billings.filters.status.label')"
                label-position="inside">
                <b-select
                  v-model="filters.status"
                  :placeholder="$t('storePages.billings.filters.status.placeholder')"
                  expanded>
                  <option :value="status" v-for="status in listOfStatus" :key="status">
                    {{
                      $t(`storePages.billings.filters.status.options.${status}`)
                    }}
                  </option>
                </b-select>
              </b-field>
            </ig-sidebar-filters>
          </div>
        </div>
      </div>

      <hr />

      <b-table
        :loading="loading"
        :data="billings"
        backend-sorting
        default-sort="createdAt"
        default-sort-direction="desc"
        paginated
        pagination-simple
        backend-pagination
        @page-change="onPageChange"
        :total="total"
        :per-page="filters.limit"
        :current-page.sync="filters.page"
        @sort="sort"
        striped
        hoverable
        scrollable
        checkable
        :checked-rows.sync="checkedRows"
      >
        <template #empty>
          <ig-empty />
        </template>

        <template #bottom-left>
          <ig-per-page
            :total-displayed="billings.length"
            :total="total"
            :perPage="filters.limit"
            @change="changePerPage"
          />
        </template>

        <b-table-column
          field="number"
          :label="$t('storePages.billings.table.number')"
          width="120"
          sortable
        >
          <template v-slot="props">
            <span>{{ String(props.row.number).padStart(6, '0') }}</span>
          </template>
        </b-table-column>

        <b-table-column
          field="createdAt"
          :label="$t('storePages.billings.table.emissionDate')"
          width="130"
          centered
          sortable
        >
          <template v-slot="props">
            {{ getFormattedDate(props.row.createdAt) }}
          </template>
        </b-table-column>

        <b-table-column
          field="createdAt"
          :label="$t('storePages.billings.table.dueDate')"
          width="130"
          centered
          sortable
        >
          <template v-slot="props">
            {{ getFormattedDate(props.row.updatedAt) }}
          </template>
        </b-table-column>

        <b-table-column
          :label="$t('storePages.billings.table.customer')"
          sortable
        >
          <template v-slot="props">
            {{ props.row.recipient.name || '-' }}
          </template>
        </b-table-column>

        <b-table-column
          width="20"
          :label="$t('storePages.billings.table.customerStateCode')"
          sortable
        >
          <template v-slot="props">
            {{ props.row.recipient.address.stateCode || '-' }}
          </template>
        </b-table-column>

        <b-table-column
          width="50"
          :label="$t('storePages.billings.table.status')"
          centered
          sortable
        >
          <template v-slot="props">
            <b-tag class="is-uppercase is-light" :type="getStatusInfo(props.row.status).type">
              {{ getStatusInfo(props.row.status).label }}
            </b-tag>
          </template>
        </b-table-column>

        <b-table-column
          width="80"
          :label="$t('storePages.billings.table.price')"
        >
          <template v-slot="props">
            {{ getFormattedPrice(props.row.total) }}
          </template>
        </b-table-column>
      </b-table>
    </div>
  </ig-page-base>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import * as Moment from 'moment';

import {
  IgPageBase,
  IgEmpty,
  IgSidebarFilters,
  IgPerPage,
} from '@integrai/ui';

const { mapState, mapActions } = createNamespacedHelpers('billings');

export default Vue.extend({
  name: 'page-billings',
  components: {
    IgPageBase,
    IgEmpty,
    IgSidebarFilters,
    IgPerPage,
  },
  data() {
    return {
      showFilters: false,
      dates: null,
      checkedRows: [],
      filters: {
        limit: 10,
        page: 1,
        sortField: 'createdAt',
        sortOrder: 'desc',
        startCreateDate: null,
        endCreateDate: null,
      },
      listOfStatus: [
        'PENDING',
        'AUTHORIZED',
        'REJECTED',
        'CANCELED',
      ],
    };
  },
  computed: {
    ...mapState([
      'loading',
      'billings',
      'total',
    ]),
  },
  methods: {
    ...mapActions([
      'getInvoices',
    ]),
    getFormattedPrice(price) {
      if (price !== undefined) {
        return price.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          style: 'currency',
          currency: 'BRL',
        });
      }

      return '-';
    },
    getPaymentMethodLabel(method) {
      const labels = {
        BOLETO: 'Boleto',
        CREDIT_CARD: 'Cartão de Cŕedito',
        PIX: 'Pix',
      };

      return labels[method] || '-';
    },
    getStatusInfo(status) {
      return {
        PENDING: {
          label: 'Pendente',
          type: 'is-warning',
        },
        AUTHORIZED: {
          label: 'Paga',
          type: 'is-success',
        },
        REJECTED: {
          label: 'Vencida',
          type: 'is-danger',
        },
        CANCELED: {
          label: 'Cancelada',
          type: 'is-danger',
        },
      }[status];
    },
    getFulfillmentStatus(status) {
      return {
        WAITING_STOCK: 'is-info',
        PROCESSING: 'is-warning',
        UNPACKED: 'is-primary',
        PACKED: 'is-info',
        DISPATCHED: 'is-success',
        IN_TRANSIT: 'is-info',
        DELIVERED: 'is-success',
        CANCELED: 'is-danger',
      }[status] || 'is-default';
    },
    getFormattedDate(date) {
      return Moment(date).format('DD/MM/YYYY HH:mm:ss');
    },
    changeDates() {
      this.$nextTick(() => {
        const [
          startCreateDate,
          endCreateDate,
        ] = this.dates || [];

        Object.assign(this.filters, {
          startCreateDate,
          endCreateDate: endCreateDate
            ? Moment(endCreateDate).endOf('day').toISOString() : undefined,
        });
      });
    },
    clearDate() {
      this.dates = null;
      this.filters.startCreateDate = null;
      this.filters.endCreateDate = null;
    },
    sort(sortField, sortOrder) {
      Object.assign(this.filters, {
        sortField,
        sortOrder,
      });

      this.getInvoiceList();
    },
    onPageChange(page) {
      Object.assign(this.filters, {
        page,
      });

      this.getInvoiceList();
    },
    changePerPage(limit) {
      Object.assign(this.filters, {
        limit,
      });

      this.getInvoiceList();
    },
    applyFilters(filters) {
      this.getInvoiceList({
        ...this.filters,
        ...filters || {},
      });
    },
    async getInvoiceList(filters) {
      await this.getInvoices(filters || this.filters);
    },
    showInvoiceError(errorMessage) {
      this.$buefy.dialog.alert(errorMessage);
    },
  },
  async beforeMount() {
    await this.getInvoiceList();
  },
});
</script>

<style lang="scss">
  .billings {
    &__title-box{
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    &__title{
      padding-bottom: 1rem;
    }
    &__line-through {
      text-decoration: line-through;
    }
  }
</style>
